import { useState, useEffect, memo, ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle, AlertTriangle, Trash2, LogOut, LucideIcon } from "lucide-react";

interface ConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "default" | "destructive" | "warning";
  icon?: LucideIcon;
  successMessage?: string;
  details?: ReactNode;
}

export const ConfirmationDialog = memo(function ConfirmationDialog({
  open,
  onOpenChange,
  onConfirm,
  title,
  description,
  confirmText = "دڵنیام",
  cancelText = "پاشگەزبوونەوە",
  variant = "default",
  icon: Icon,
  successMessage,
  details,
}: ConfirmationDialogProps) {
  const [isConfirming, setIsConfirming] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // ڕیسێت کردنی state کاتێک دایەلۆگ دادەخرێت
  useEffect(() => {
    if (!open) {
      setShowSuccess(false);
      setIsConfirming(false);
    }
  }, [open]);

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await onConfirm();
      if (successMessage) {
        setShowSuccess(true);
        setTimeout(() => {
          onOpenChange(false);
        }, 1500);
      } else {
        onOpenChange(false);
      }
    } catch {
      setIsConfirming(false);
    }
  };

  // رەنگەکان بەپێی variant
  const getVariantStyles = () => {
    switch (variant) {
      case "destructive":
        return {
          iconBg: "bg-destructive/10",
          iconColor: "text-destructive",
          buttonClass: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
          successBg: "bg-destructive/10",
          successColor: "text-destructive",
        };
      case "warning":
        return {
          iconBg: "bg-amber-100 dark:bg-amber-900/30",
          iconColor: "text-amber-600 dark:text-amber-400",
          buttonClass: "bg-amber-600 text-white hover:bg-amber-700",
          successBg: "bg-amber-100 dark:bg-amber-900/30",
          successColor: "text-amber-600 dark:text-amber-400",
        };
      default:
        return {
          iconBg: "bg-primary/10",
          iconColor: "text-primary",
          buttonClass: "",
          successBg: "bg-green-100 dark:bg-green-900/30",
          successColor: "text-green-600 dark:text-green-400",
        };
    }
  };

  const styles = getVariantStyles();

  // ئایکۆنی پێشوەختە بەپێی variant
  const getDefaultIcon = () => {
    switch (variant) {
      case "destructive":
        return Trash2;
      case "warning":
        return AlertTriangle;
      default:
        return CheckCircle;
    }
  };

  const DisplayIcon = Icon || getDefaultIcon();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-w-[calc(100%-2rem)]" showCloseButton={false}>
        {showSuccess && successMessage ? (
          // پیشاندانی سەرکەوتوو
          <div className="flex flex-col items-center justify-center py-8">
            <div className={`h-20 w-20 rounded-full ${styles.successBg} flex items-center justify-center mb-4 animate-in zoom-in duration-300`}>
              <CheckCircle className={`h-12 w-12 ${styles.successColor}`} />
            </div>
            <h3 className={`text-xl font-bold ${styles.successColor} mb-2`}>
              سەرکەوتوو بوو!
            </h3>
            <p className="text-sm text-muted-foreground text-center">
              {successMessage}
            </p>
          </div>
        ) : (
          // فۆڕمی پشتڕاستکردنەوە
          <>
            <DialogHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className={`h-12 w-12 rounded-full ${styles.iconBg} flex items-center justify-center`}>
                  <DisplayIcon className={`h-6 w-6 ${styles.iconColor}`} />
                </div>
                <div>
                  <DialogTitle className="text-right text-lg">
                    {title}
                  </DialogTitle>
                  <DialogDescription className="text-right text-xs">
                    {description}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            {details && (
              <div className="py-4">
                {details}
              </div>
            )}

            {/* دوگمەکان */}
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isConfirming}
                className="flex-1"
              >
                {cancelText}
              </Button>
              <Button
                type="button"
                onClick={handleConfirm}
                disabled={isConfirming}
                className={`flex-1 ${styles.buttonClass}`}
              >
                {isConfirming ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent ml-2" />
                    چاوەڕوان بە...
                  </>
                ) : (
                  confirmText
                )}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
});

// دایەلۆگی چوونەدەرەوە
interface LogoutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void | Promise<void>;
}

export const LogoutDialog = memo(function LogoutDialog({
  open,
  onOpenChange,
  onConfirm,
}: LogoutDialogProps) {
  return (
    <ConfirmationDialog
      open={open}
      onOpenChange={onOpenChange}
      onConfirm={onConfirm}
      title="چوونەدەرەوە"
      description="دڵنیایت دەتەوێت لە ئەکاونتەکەت بچیتە دەرەوە؟"
      confirmText="بەڵێ، دەرچۆ"
      cancelText="نەخێر"
      variant="warning"
      icon={LogOut}
      successMessage="بە سەرکەوتوویی چوویتە دەرەوە"
    />
  );
});

// دایەلۆگی سڕینەوە
interface DeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void | Promise<void>;
  itemName?: string;
  itemType?: string;
}

export const DeleteDialog = memo(function DeleteDialog({
  open,
  onOpenChange,
  onConfirm,
  itemName,
  itemType = "ئەم بڕگەیە",
}: DeleteDialogProps) {
  return (
    <ConfirmationDialog
      open={open}
      onOpenChange={onOpenChange}
      onConfirm={onConfirm}
      title="سڕینەوە"
      description={itemName ? `دڵنیایت دەتەوێت "${itemName}" بسڕیتەوە؟` : `دڵنیایت دەتەوێت ${itemType} بسڕیتەوە؟`}
      confirmText="بەڵێ، بیسڕەوە"
      cancelText="نەخێر"
      variant="destructive"
      icon={Trash2}
    />
  );
});
